import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useGlobalization } from '@Translations';

import { Help, KeyboardArrowDown, KeyboardArrowUp, Warning, WarningAmberTwoTone } from '@mui/icons-material';
import { Badge, Box, Link as MaterialLink, Stack, Typography } from '@mui/material';
import {
	Button,
	DataGrid,
	GRID_DETAIL_PANEL_TOGGLE_FIELD,
	gridDetailPanelExpandedRowIdsSelector,
	GridRenderCellParams,
	NavigationPrompt,
	palette,
	StylovyzeFormV2,
	Text,
	useGridApiContext,
	useGridSelector,
	error,
	GridRowId,
	GridLogicOperator,
	openDrawer,
	GridFilterModel,
	GridSortModel,
} from '@innovyze/stylovyze';

import { listDeteriorationModelCurveConfigsAction, getGlobalUndefinedCountAction, setPaginationState } from '@Actions';
import {
	selectDeteriorationModel,
	selectDeteriorationModelCurveConfigs,
	selectIndustryStandardCurves,
	selectGlobalUndefinedCount,
	selectModelPaginationState,
} from '@Selectors';
import {
	CurveMethod,
	DeteriorationModelStatus,
	GetDeteriorationModelCurveConfigsPaginatedRequest,
	WeibullFormulation,
} from '@Types';
import { refreshTrainResults } from '@Services/deteriorationModels.services';

import { CurveConfiguration, BulkChangeDeteriorationMethodDialog } from './curveConfiguration';
import { ALL_CURVE_CONFIGS_SLICE } from './curveConfiguration/constants';
import HelpDrawer, { HELP_DRAWER } from '@Components/HelpPageDrawer';
import { DarkInfoTooltip } from '@Components/deteriorationModel/configurationTab/DarkInfoTooltip';
import { WEAVE_FONT } from '@Utils/fonts';

const curveMethods = [
	{
		label: 'Undefined',
		value: CurveMethod.UNDEFINED,
	},
	{
		label: 'From Failure Definition',
		value: CurveMethod.FAILURE_DEFINITION,
	},
	{
		label: 'Custom Curve',
		value: CurveMethod.CUSTOM_CURVE,
	},
	{
		label: 'Industry Standard Curve',
		value: CurveMethod.INDUSTRY_CURVE,
	},
	{
		label: 'Copy Curve from Another Group',
		value: CurveMethod.COPIED_CURVE,
	},
	{
		label: 'One Expected Useful Life',
		value: CurveMethod.ONE_EXPECTED_LIFE,
	},
];

const CustomDetailPanelToggle = (props: GridRenderCellParams) => {
	const { t } = useGlobalization();
	const { id, row } = props;
	const apiRef = useGridApiContext();

	const expandedRowIds = useGridSelector(apiRef, gridDetailPanelExpandedRowIdsSelector);

	const isExpanded = expandedRowIds.includes(id);
	const hasUpdates = row.updatedCurveMethodData !== null;

	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		apiRef.current.toggleDetailPanel(id);
	};

	return (
		<Badge color="primary" variant="dot" invisible={!hasUpdates}>
			<Button color="primary" onClick={handleToggle}>
				{isExpanded ? (
					<>
						<Text>{t('Hide Method')}</Text>
						<KeyboardArrowUp />
					</>
				) : (
					<>
						<Text>{t('Show Method')}</Text>
						<KeyboardArrowDown />
					</>
				)}
			</Button>
		</Badge>
	);
};

interface AssetGroupsCurveDeteriorationMethodsTableProps {
	onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
}

export const AssetGroupsCurveDeteriorationMethodsTable = ({
	onUnsavedChangesChange,
}: AssetGroupsCurveDeteriorationMethodsTableProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();

	const industryStandardCurves = selectIndustryStandardCurves();
	const deteriorationModel = selectDeteriorationModel();
	const currentDeteriorationModel = deteriorationModel.deteriorationModel;
	const globalUndefinedCountData = selectGlobalUndefinedCount();
	const modelId = currentDeteriorationModel?.id || '';

	const storedPaginationState = selectModelPaginationState(modelId);

	// Fetch all records initially for client-side pagination
	const fetchAllRequest: GetDeteriorationModelCurveConfigsPaginatedRequest = {
		modelId,
		page: 1,
		slice: ALL_CURVE_CONFIGS_SLICE,
		sortKey: 'assetGroupName',
		sortDescending: true,
		searchValue: null,
		filter: null,
		filterModel: { items: [] } as GridFilterModel,
	};

	useEffect(() => {
		if (modelId) {
			dispatch(listDeteriorationModelCurveConfigsAction(fetchAllRequest));
		}
	}, [modelId]);

	const curveConfigs = selectDeteriorationModelCurveConfigs();

	const [paginationModel, setPaginationModel] = useState({
		page: storedPaginationState?.configuration?.page ? storedPaginationState.configuration.page - 1 : 0,
		pageSize: storedPaginationState?.configuration?.slice || 10,
	});

	const [sortModel, setSortModel] = useState<GridSortModel>([
		{
			field: storedPaginationState?.configuration?.sortKey || 'assetGroupName',
			sort: storedPaginationState?.configuration?.sortDescending ? 'desc' : 'asc',
		},
	]);

	const [filterModel, setFilterModel] = useState<GridFilterModel>(() => {
		const stored = storedPaginationState?.configuration?.filterModel;
		if (stored) {
			return {
				items: stored.items,
				logicOperator: (stored.logicOperator as GridLogicOperator) || GridLogicOperator.And,
			};
		}
		return { items: [], logicOperator: GridLogicOperator.And };
	});

	const [searchValue, setSearchValue] = useState(storedPaginationState?.configuration?.searchValue || '');

	const [openBulkChangeDialog, setOpenBulkChangeDialog] = useState(false);
	const [selectedCurveConfigs, setSelectedCurveConfigs] = useState<GridRowId[]>([]);

	const draftsRef = useRef<Record<string, { updatedCurveMethodData: object | null; selectedCurveMethod: string }>>({});

	useEffect(() => {
		dispatch(
			setPaginationState({
				modelId,
				tab: 'configuration',
				paginationRequest: {
					modelId,
					page: paginationModel.page + 1,
					slice: paginationModel.pageSize,
					sortKey: sortModel[0]?.field || 'assetGroupName',
					sortDescending: sortModel[0]?.sort === 'desc',
					searchValue: searchValue || null,
					filter: null,
					filterModel,
				},
			}),
		);
	}, [paginationModel, sortModel, filterModel, searchValue, modelId, dispatch]);

	useEffect(() => {
		if (!currentDeteriorationModel?.id) return;

		dispatch(
			getGlobalUndefinedCountAction({
				modelId: currentDeteriorationModel.id,
			}),
		);
	}, [currentDeteriorationModel?.id, dispatch]);

	const isDraft = currentDeteriorationModel?.status === DeteriorationModelStatus.draft;

	const handleRefresh = async () => {
		try {
			await refreshTrainResults(currentDeteriorationModel?.id || '');
			dispatch(listDeteriorationModelCurveConfigsAction(fetchAllRequest));
			dispatch(getGlobalUndefinedCountAction({ modelId: currentDeteriorationModel?.id || '' }));
		} catch (e) {
			console.error('Error refreshing train results:', e);
			dispatch(error(t('Unable to refresh train results')));
		}
	};

	return (
		<>
			<HelpDrawer drawerID={HELP_DRAWER.SETUP_DETERIORATION_METHOD} />
			<Stack spacing={0.5} sx={{ mt: 2, mb: -1 }}>
				<Typography
					sx={{ fontFamily: WEAVE_FONT, fontSize: '1.25rem', fontWeight: 700, color: '#111827', lineHeight: 1.4 }}>
					{t('Assign survival curve method to groups')}
				</Typography>
				<Typography sx={{ fontFamily: WEAVE_FONT, fontSize: '0.8125rem', color: '#6b7280' }}>
					{t(
						'Asset group needs a survival curve to model how its assets deteriorate over time. Assign a curve only to the groups you want included in the model.',
					)}{' '}
					<MaterialLink
						style={{ cursor: 'pointer', textDecoration: 'none', fontFamily: WEAVE_FONT, fontSize: '0.8125rem' }}
						onClick={() => {
							dispatch(openDrawer(HELP_DRAWER.SETUP_DETERIORATION_METHOD));
						}}>
						{t('Learn more')}
					</MaterialLink>
				</Typography>
			</Stack>
			{globalUndefinedCountData.count > 0 && (
				<Box
					sx={{
						display: 'flex',
						width: '100%',
						overflow: 'hidden',
						border: '1px solid #FCC776',
						mb: -1,
					}}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: '32px',
							flexShrink: 0,
							backgroundColor: '#FAA21B',
						}}>
						<Warning sx={{ color: '#FFFEFE', fontSize: '20px' }} />
					</Box>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							backgroundColor: 'rgba(250,162,27,0.2)',
							padding: '8px 12px',
							flex: 1,
						}}>
						<Typography sx={{ color: '#3C3C3C', fontSize: '0.75rem', fontWeight: 500 }}>
							{t(
								'You have {{undefinedCount}} group(s) with an undefined survival curve method. These will be excluded from the model unless you select a method. ',
								{ undefinedCount: globalUndefinedCountData.count },
							)}
						</Typography>
					</Box>
				</Box>
			)}
			<StylovyzeFormV2 initialValues={[]} onSubmit={() => undefined}>
				{({ values, setValues }) => {
					useEffect(() => {
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						const newValues: Record<string, any> = {};

						for (const row of curveConfigs.rows) {
							const existingDraft = draftsRef.current[row.id];

							newValues[row.id] = {
								...row,
								updatedCurveMethodData: existingDraft?.updatedCurveMethodData ?? null,
								selectedCurveMethod: existingDraft?.updatedCurveMethodData
									? existingDraft.selectedCurveMethod
									: row.selectedCurveMethod,
							};
						}

						setValues(newValues);
					}, [curveConfigs.rows]);

					const hasUnsavedChanges = Object.values(values).some(row => row?.updatedCurveMethodData !== null);

					useEffect(() => {
						Object.keys(values).forEach(rowId => {
							const row = values[rowId];
							if (row?.updatedCurveMethodData) {
								draftsRef.current[rowId] = {
									updatedCurveMethodData: row.updatedCurveMethodData,
									selectedCurveMethod: row.selectedCurveMethod,
								};
							} else {
								delete draftsRef.current[rowId];
							}
						});
					}, [values]);

					useEffect(() => {
						onUnsavedChangesChange?.(hasUnsavedChanges);
					}, [hasUnsavedChanges]);

					const showDraftWarning = isDraft && !hasUnsavedChanges;

					return (
						<>
							<NavigationPrompt
								title={t('Configuration in Draft Status')}
								content={t(
									'Your configuration is in draft status. You can leave and return later to run the model, or stay to continue editing.',
								)}
								confirmText={t('Leave')}
								cancelText={t('Stay')}
								navigate={nextLocation => {
									history.push(nextLocation);
								}}
								when={showDraftWarning}
								shouldBlockNavigation={() => showDraftWarning}
								shouldBlockReload={showDraftWarning}
							/>
							<BulkChangeDeteriorationMethodDialog
								open={openBulkChangeDialog}
								onCancel={() => setOpenBulkChangeDialog(false)}
								cancelText={t('Cancel')}
								confirmText={t('Select')}
								selectedCurveConfigs={selectedCurveConfigs as string[]}
								selectedCurveConfigsData={selectedCurveConfigs.map(id => values[id]).filter(Boolean)}
								modelId={currentDeteriorationModel?.id || ''}
								industryStandardCurves={industryStandardCurves}
							/>
							<DataGrid
								// loading
								loading={curveConfigs.isLoading}
								skeletonRowCount={paginationModel.pageSize}
								// columns and headers
								columns={[
									{
										field: 'assetGroupName',
										headerName: t('Asset Group'),
										renderHeader: () => <Text style={{ fontWeight: 'bold' }}>{t('Asset Group')}</Text>,
										flex: 1,
										type: 'string',
										filterable: true,
										align: 'left',
										headerAlign: 'left',
									},
									{
										field: 'assetGroupDescription',
										headerName: t('Description'),
										renderHeader: () => <Text style={{ fontWeight: 'bold' }}>{t('Description')}</Text>,
										flex: 1.5,
										type: 'string',
										filterable: true,
										align: 'left',
										headerAlign: 'left',
									},
									{
										field: 'assetCount',
										headerName: t('Asset Count'),
										renderHeader: () => <Text style={{ fontWeight: 'bold' }}>{t('Asset Count')}</Text>,
										flex: 1,
										type: 'number',
										filterable: true,
										align: 'right',
										headerAlign: 'right',
									},
									{
										field: 'failureCount',
										headerName: t('Failure Count'),
										renderHeader: () => (
											<Stack direction="row" spacing={1} alignItems="center">
												<Text style={{ fontWeight: 'bold' }}>{t('Failures')}</Text>
												<DarkInfoTooltip
													heading={t('Failed assets')}
													body={[
														// Deliberately does not promise that each of these contributes a data point, and does
														// not present 5 as sufficient. The trainers drop any failure whose age cannot be
														// computed, so this is an upper bound on what a fit actually uses -- a group can show 8
														// here and still be told it has too few usable failures, and the old wording made that
														// read as a contradiction one hover apart.
														t(
															'The number of assets in this group that experienced a failure, per the selected Failure Definition.',
														),
														t(
															'A failure is only used to build the curve if the age at failure can be calculated from the installation and failure dates, so fewer than this number may be usable. At least 5 usable failures are needed.',
														),
														t('If no Failure Definition is selected, this column will be empty.'),
													]}>
													<Help sx={{ fontSize: 'small', color: palette.secondary.main }} />
												</DarkInfoTooltip>
											</Stack>
										),
										flex: 1,
										type: 'number',
										filterable: true,
										align: 'right',
										headerAlign: 'right',
										renderCell: params => (currentDeteriorationModel?.failureDefinitionId ? params.value : '-'),
									},
									{
										field: 'selectedCurveMethod',
										headerName: t('Survival Curve Method'),
										renderHeader: () => (
											<Stack direction="row" spacing={1} alignItems="center">
												<Text style={{ fontWeight: 'bold' }}>{t('Survival Curve Method')}</Text>
												<DarkInfoTooltip
													heading={t('Survival Curve Method')}
													body={t(
														'Survival Curve Method defines how asset lifespan is modeled for this asset group - via past failure type data, industry standard curves, a custom manually defined curve, or a single expected useful life value. To change or add a method, click on Show Method.',
													)}>
													<Help sx={{ fontSize: 'small', color: palette.secondary.main }} />
												</DarkInfoTooltip>
											</Stack>
										),
										flex: 1,
										type: 'singleSelect',
										filterable: true,
										valueOptions: curveMethods,
										align: 'right',
										headerAlign: 'right',
										renderCell: params =>
											params.value === CurveMethod.UNDEFINED ? (
												<Stack direction="row" spacing={0.5} alignItems="center">
													<DarkInfoTooltip
														heading={t('Undefined Survival Curve Method')}
														body={t(
															'Asset groups with undefined Survival Curve Method will be excluded from the model unless you select a method. To do this, click on Show Method and select a data option. The method may be undefined because: You did not select a failure definition. If you want to add one now, select the Edit button above. There are not enough asset failures (min. 5 required) to generate a curve for the group using the selected failure definition. You can use industry standard or custom curves instead.',
														)}>
														<WarningAmberTwoTone
															sx={{ fontSize: 18, cursor: 'pointer', verticalAlign: 'middle', color: '#FAA21B' }}
														/>
													</DarkInfoTooltip>
													<Text
														style={{
															color: '#FAA21B',
															fontSize: '0.875rem',
															lineHeight: 1,
															fontWeight: 'bold',
														}}>
														{t('Undefined')}
													</Text>
												</Stack>
											) : (
												params.formattedValue
											),
									},
									{
										field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
										renderCell: params => <CustomDetailPanelToggle {...params} />,
										flex: 1,
										headerName: '',
										sortable: false,
										filterable: false,
										align: 'center',
									},
								]}
								disableColumnMenu
								disableColumnPinning
								disableColumnReorder
								// footer
								footerHelper={(() => {
									const filteredRows = Object.values(values).filter(row => {
										if (!searchValue) return true;
										const searchLower = searchValue.toLowerCase();
										return row.assetGroupName?.toLowerCase().includes(searchLower);
									});
									const total = filteredRows.length;
									const from = total === 0 ? 0 : paginationModel.page * paginationModel.pageSize + 1;
									const to = Math.min((paginationModel.page + 1) * paginationModel.pageSize, total);
									return t('{{from}}-{{to}} of {{total}}', { from, to, total });
								})()}
								// toolbar actions
								actionsMenu={{
									menuItems: [
										{
											title: t('Change Survival Curve Method'),
											onClick: () => setOpenBulkChangeDialog(true),
											disabled: selectedCurveConfigs.length === 0,
										},
									],
								}}
								onRowSelectionModelChange={selectedRows => setSelectedCurveConfigs(selectedRows)}
								rowSelectionModel={selectedCurveConfigs}
								// search and filter
								search={{
									value: searchValue,
									onChange: (value: string) => {
										setSearchValue(value);
									},
									onCancelSearch: () => {
										setSearchValue('');
									},
									placeholder: t('Search by Asset Group Name'),
								}}
								filterToolbar
								filterModel={filterModel}
								onFilterModelChange={model => {
									setFilterModel(model);
								}}
								slotProps={{
									filterPanel: {
										logicOperators: [GridLogicOperator.And],
									},
								}}
								// refresh
								refreshButton={{
									onRefresh: handleRefresh,
								}}
								// sorting
								sortModel={sortModel}
								onSortModelChange={model => {
									setSortModel(model);
								}}
								// rows
								rows={Object.values(values).filter(row => {
									if (!searchValue) return true;
									const searchLower = searchValue.toLowerCase();
									return row.assetGroupName?.toLowerCase().includes(searchLower);
								})}
								getDetailPanelHeight={() => 'auto'}
								getDetailPanelContent={({ row }) => {
									const rowValue = values[row.id];
									return (
										<CurveConfiguration
											modelId={currentDeteriorationModel?.id || ''}
											curveConfigId={rowValue.id}
											industryStandardCurves={industryStandardCurves}
											failureCount={rowValue.failureCount}
											weibullFormulation={
												currentDeteriorationModel?.weibullFormulation ?? WeibullFormulation.TWO_PARAMETER
											}
										/>
									);
								}}
								checkboxSelection
								checkboxSelectionVisibleOnly
								// pagination
								pagination
								pageSizeOptions={[5, 10, 15, 25, 50, 100]}
								paginationModel={paginationModel}
								onPaginationModelChange={model => {
									setPaginationModel(model);
								}}
							/>
						</>
					);
				}}
			</StylovyzeFormV2>
		</>
	);
};
